import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  BreadcrumbGroup,
  Button,
  ContentLayout,
  Flashbar,
  FlashbarProps,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { Event, Shift } from '@amzn/red-velvet-api';
import { SignupTableShiftHeader } from './signupTableShiftHeader';
import { SignupTableForShift } from './signupTableForShift';
import { useAllSignupsByShiftId } from '../../hooks/signup/useSignupsByShiftId';
import { useRefreshSignupCapacity } from '../../hooks/signup/useRefreshSignupCapacity';
import { useShiftFromUrlParams } from '../../hooks/shifts/shiftFromUrlParams';
import { getValueFromSearchParam } from '../../utils/url';
import { BulkSignupResult, isShiftManageSignupBlocked } from '../../utils/signupUtils';
import { shouldBlockAsVCDraft } from '../../utils/eventUtils';
import { UnsupportedSignupLayout } from './coordinatorManageSignupsPageCommon';
import { publishKatalMetric } from '../katalAnalytics';
import { SIGNUP_MANAGEMENT_SINGLE_SHIFT_PAGE_VIEW } from '../../utils/constants';
import { AppOutletContext } from '../../App';

interface LocationState {
  event?: Event;
  shift?: Shift;
}

interface CoordinatorManageSignupsPageForShiftProps {
  navigation?: React.ReactNode;
}

export function CoordinatorManageSignupsPageForShift({
  navigation,
}: CoordinatorManageSignupsPageForShiftProps) {
  const { t } = useTranslation(['signup', 'activity', 'translation']);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const shiftId = getValueFromSearchParam(searchParams, 'shiftId', '');
  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const urlFeatures = searchParams.get('features');
  const refreshSignupCapacity = useRefreshSignupCapacity(eventId);
  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  // No navigation prop means the shared (new nav) layout owns the page frame.
  const isNewNav = !navigation;
  const outletContext = useOutletContext<AppOutletContext | null>();
  // Try to get event/shift from route state first
  const locationState = location.state as LocationState | null;
  const eventFromState = locationState?.event;
  const shiftFromState = locationState?.shift;

  // Always fetch fresh data from API
  const {
    event: eventFromApi,
    shift: shiftFromApi,
    isFetched: isEventShiftCoordinatorFetched,
    isError: isEventShiftCoordinatorFetchedWithError,
  } = useShiftFromUrlParams(true);

  // Always keep data refresh: Use API data when available, fall back to route state while loading
  const event = eventFromApi || eventFromState;
  const shift = shiftFromApi || shiftFromState;
  const isEventShiftCoordinatorLoading = !isEventShiftCoordinatorFetched;

  // Fetch signups
  const {
    signups,
    isLoading: isSignupsForShiftLoading,
    isError: isSignupsForShiftError,
  } = useAllSignupsByShiftId(shiftId);

  const dismissFlashItem = useCallback(
    (id: string) => setFlashItems((prev) => prev.filter((item) => item.id !== id)),
    []
  );

  const handleBulkSignupComplete = useCallback(
    (result: BulkSignupResult) => {
      const items: FlashbarProps.MessageDefinition[] = [];

      if (result.succeededCount > 0) {
        items.push({
          type: 'success',
          dismissible: true,
          onDismiss: () => dismissFlashItem('bulk-signup-success'),
          content: t('bulkSignupAlerts.successMessage', {
            count: result.succeededCount,
            eventTitle: event?.title || '',
            shiftName: shift?.name || '',
            ns: 'signup',
          }),
          id: 'bulk-signup-success',
        });
      }

      if (result.noAccessUsers.length > 0) {
        const aliases = result.noAccessUsers
          .map((u) => u.identifierValue)
          .filter(Boolean)
          .join(', ');

        items.push({
          type: 'error',
          dismissible: true,
          onDismiss: () => dismissFlashItem('bulk-signup-no-access'),
          content: t('bulkSignupAlerts.noAccessMessage', {
            count: result.noAccessUsers.length,
            ns: 'signup',
          }),
          action: (
            <Button onClick={() => navigator.clipboard?.writeText(aliases)?.catch(() => undefined)}>
              {t('bulkSignupAlerts.copySkippedAliases', { ns: 'signup' })}
            </Button>
          ),
          id: 'bulk-signup-no-access',
        });
      }

      setFlashItems(items);

      refreshSignupCapacity();
    },
    [t, event?.title, shift?.name, refreshSignupCapacity, dismissFlashItem]
  );

  useEffect(() => {
    publishKatalMetric(
      SIGNUP_MANAGEMENT_SINGLE_SHIFT_PAGE_VIEW,
      `eventId=${eventId}:shiftId=${shiftId}`
    );
  }, [eventId, shiftId]);

  const isBlocked = shouldBlockAsVCDraft(event);
  const isDeleted = !!event?.vcDeleted;
  useEffect(() => {
    if (isBlocked) {
      publishKatalMetric('createEvent_draftAlertManageSignups', event?.eventId || '');
    }
  }, [isBlocked, event?.eventId]);

  // New nav: publish bulk-upload results (how many signed up, how many failed and why) into the
  // root AppLayout's notifications region. Hiding the legacy AppLayout to drop its breadcrumbs also
  // dropped its notifications slot, so these results disappeared entirely (CR-291797692 rev 8).
  // Keyed on flashItems (the data) rather than the rendered element so the effect doesn't re-run
  // every render, and cleared on unmount so results don't leak onto the next page.
  useEffect(() => {
    if (!isNewNav || !outletContext) return;
    outletContext.setNotifications(flashItems.length > 0 ? <Flashbar items={flashItems} /> : null);
    return () => outletContext.setNotifications(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, flashItems]);

  if (isShiftManageSignupBlocked(event, shift)) {
    // In new nav (no navigation prop) the root AppLayout owns the page frame, so pass contentOnly
    // to avoid rendering a second <main> region here (CR-291797692 rev 7).
    return <UnsupportedSignupLayout contentOnly={!navigation} />;
  }

  const notifications = flashItems.length > 0 ? <Flashbar items={flashItems} /> : undefined;

  const content = (
    <ContentLayout>
      <SpaceBetween direction="vertical" size="l">
        <SignupTableShiftHeader
          event={event}
          shift={shift}
          isLoading={isEventShiftCoordinatorLoading}
          isError={isEventShiftCoordinatorFetchedWithError}
        />
        {isDeleted ? (
          <Flashbar
            items={[
              {
                type: 'error',
                header: t('draftBlock.deletedEvent.header', { ns: 'signup' }),
                content: t('draftBlock.deletedEvent.content', { ns: 'signup' }),
              },
            ]}
          />
        ) : isBlocked ? (
          <Flashbar
            items={[
              {
                type: 'warning',
                header: t('draftBlock.warningHeader', { ns: 'signup' }),
                content: t('draftBlock.warning', { ns: 'signup' }),
              },
            ]}
          />
        ) : (
          <SignupTableForShift
            signups={signups}
            isLoading={isSignupsForShiftLoading}
            isError={isSignupsForShiftError}
            event={event}
            shift={shift}
            onBulkSignupComplete={handleBulkSignupComplete}
          />
        )}
      </SpaceBetween>
    </ContentLayout>
  );

  // New navigation (no navigation prop passed): return content only. Bulk-upload result
  // notifications are published to the root AppLayout's notifications region by the effect above,
  // so they render in the proper (sticky) notification area rather than inside the content.
  if (isNewNav) {
    return content;
  }

  // Legacy: wrap in AppLayout
  return (
    <AppLayout
      footerSelector=".footer"
      navigation={navigation}
      toolsHide={true}
      notifications={notifications}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            {
              text: t('breadcrumbs.home', { ns: 'translation' }),
              href: `/${urlFeatures ? '?features=' + urlFeatures : ''}`,
            },
            {
              text: t('breadcrumbs.coordinatorActivity', { ns: 'activity' }),
              href: `/coordinator-activity${urlFeatures ? '?features=' + urlFeatures : ''}`,
            },
            { text: shift?.name || '', href: '#' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />
      }
      content={content}
      contentType="table"
    />
  );
}
